import { useState, useCallback, useRef } from 'react';
import { client } from '../../services/api';
import { isAbortError } from '../../utils/abortableRequest';
import type { PatientMatchResult } from './types';

interface UsePatientMatchingOptions {
  kinnserClinicId: string | null | undefined;
}

interface PatientMatchData {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  mrn?: string | null;
  dob?: string | null;
  ssn?: string | null;
  address?: string | null;
  zipCode?: string | null;
  gender?: string | null;
  email?: string | null;
  city?: string | null;
  state?: string | null;
}

interface UsePatientMatchingReturn {
  patientMatches: PatientMatchResult[];
  isMatchingPatients: boolean;
  patientMatchModalOpen: boolean;
  setPatientMatchModalOpen: (open: boolean) => void;
  checkPatientMatch: (patientData: PatientMatchData) => Promise<void>;
  resetMatches: () => void;
}

/**
 * Hook for checking patient matches in Roger Doc.
 * Manages patient match modal state and handles API calls with abort controller.
 */
export function usePatientMatching({
  kinnserClinicId,
}: UsePatientMatchingOptions): UsePatientMatchingReturn {
  const [patientMatches, setPatientMatches] = useState<PatientMatchResult[]>(
    []
  );
  const [isMatchingPatients, setIsMatchingPatients] = useState(false);
  const [patientMatchModalOpen, setPatientMatchModalOpen] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const checkPatientMatch = useCallback(
    async (patientData: PatientMatchData) => {
      const firstName = patientData.firstName.trim();
      const lastName = patientData.lastName.trim();

      if (!kinnserClinicId) return;

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const currentController = new AbortController();
      abortControllerRef.current = currentController;

      // Map gender: M -> "1", F -> "2", else -> ""
      const mapGender = (gender: string | null | undefined): string => {
        if (!gender) return '';
        const normalized = gender.toUpperCase();
        if (normalized === 'M') return '1';
        if (normalized === 'F') return '2';
        return '';
      };

      // Extract middle initial (first character of middle name)
      const middleInitial =
        patientData.middleName?.trim().charAt(0).toUpperCase() || '';

      try {
        setIsMatchingPatients(true);
        const response = await client.request({
          method: 'POST',
          url: `/rogerdoc/api/patient/match/${kinnserClinicId}`,
          body: {
            first_name: firstName,
            middle_initial: middleInitial,
            last_name: lastName,
            mrn: patientData.mrn?.trim() || '',
            dob: patientData.dob?.trim() || '',
            ssn: patientData.ssn?.trim() || '',
            address: patientData.address?.trim() || '',
            zip_code: patientData.zipCode?.trim() || '',
            gender: mapGender(patientData.gender),
            email: patientData.email?.trim() || '',
            city: patientData.city?.trim() || '',
            state: patientData.state?.trim() || '',
          },
          signal: currentController.signal,
        });

        const matches = response.data as PatientMatchResult[];
        setPatientMatches(matches);
        if (matches && matches.length > 0) {
          setPatientMatchModalOpen(true);
        }
      } catch (error: unknown) {
        // Ignore abort errors, log others
        if (!isAbortError(error)) {
          console.error('Patient match error:', error);
        }
      } finally {
        // Only set loading to false if this is still the current request
        if (abortControllerRef.current === currentController) {
          setIsMatchingPatients(false);
        }
      }
    },
    [kinnserClinicId]
  );

  const resetMatches = useCallback(() => {
    setPatientMatches([]);
    setPatientMatchModalOpen(false);
    setIsMatchingPatients(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    patientMatches,
    isMatchingPatients,
    patientMatchModalOpen,
    setPatientMatchModalOpen,
    checkPatientMatch,
    resetMatches,
  };
}
